import { useEffect, useState } from "react";
import { api } from "../api";
import { Pill, actionPillKind, actionLabel } from "../components/Pill";
import { RedactedPreview } from "../components/RedactedPreview";
import { RiskPill } from "../components/RiskPill";
import { ToolBadge, ToolIcon, toolLabel } from "../components/ToolBadge";

type Filter = { actionResult?: string; destinationTool?: string };

export function EventsPage() {
  const [filter, setFilter] = useState<Filter>({});
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    const res = await api.listEvents({ limit: 100, filter });
    setEvents(res.events);
    setLoading(false);
  }
  useEffect(() => { load(); }, [filter.actionResult, filter.destinationTool]);

  return (
    <>
      <header className="sg-page-header">
        <h1 className="sg-page-title">Events</h1>
        <div className="sg-page-sub">Log · Every Evaluated Prompt</div>
      </header>

      <div className="sg-filters">
        {["block", "suggest_rewrite", "warn", "allow"].map((a) => (
          <button
            key={a}
            className={`sg-chip ${filter.actionResult === a ? "active" : ""}`}
            onClick={() => setFilter((f) => ({ ...f, actionResult: f.actionResult === a ? undefined : a }))}
          >{actionLabel(a)}</button>
        ))}
        <div style={{ width: 16 }} />
        {[
          { key: "chatgpt", label: "ChatGPT" },
          { key: "claude", label: "Claude" },
          { key: "gemini", label: "Gemini" },
          { key: "copilot", label: "Copilot" },
        ].map((t) => (
          <button
            key={t.key}
            className={`sg-chip ${filter.destinationTool === t.key ? "active" : ""}`}
            onClick={() => setFilter((f) => ({ ...f, destinationTool: f.destinationTool === t.key ? undefined : t.key }))}
          >
            <ToolIcon tool={t.key} size={12} />
            <span style={{ marginLeft: 6 }}>{t.label}</span>
          </button>
        ))}
      </div>

      <div className="sg-log">
        {loading && <div className="sg-empty">Loading…</div>}
        {!loading && events.length === 0 && (
          <div className="sg-empty sg-empty-cta">
            {filter.actionResult || filter.destinationTool ? (
              <>
                <strong>No matching events.</strong>
                Try clearing filters, or wait for new activity from the extension.
              </>
            ) : (
              <>
                <strong>No events yet.</strong>
                <span style={{ display: "block", marginTop: 6, color: "var(--sg-ink-3)" }}>
                  Install the extension and run a prompt on ChatGPT, Claude, Gemini or Copilot. Events appear here within seconds.
                </span>
                <div className="sg-row" style={{ gap: 8, marginTop: 14, justifyContent: "center" }}>
                  <a className="sg-btn sg-btn-primary sg-btn-sm" href="https://www.ventrin.com/download.html" target="_blank" rel="noopener">Install extension</a>
                  <a className="sg-btn sg-btn-sm" href="/policies">Tune policies</a>
                </div>
              </>
            )}
          </div>
        )}
        {events.map((e) => <EventRecord key={e.id} event={e} />)}
      </div>
    </>
  );
}

function EventRecord({ event }: { event: any }) {
  const [revealed, setRevealed] = useState<string | null>(null);
  const [revealing, setRevealing] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  async function toggleReveal() {
    if (revealed) { setRevealed(null); return; }
    if (!event.encryptedFullPrompt) return;
    const reason = window.prompt("Reason for revealing this prompt (audited):");
    if (!reason || reason.trim().length < 3) {
      setToast("A reason (min 3 chars) is required.");
      setTimeout(() => setToast(null), 2800);
      return;
    }
    setRevealing(true);
    setToast("Audited action — reveal is logged.");
    setTimeout(() => setToast(null), 2800);
    try {
      const res = await api.revealPrompt({ eventId: event.id, reason: reason.trim() });
      setRevealed(res.prompt);
    } catch (ex: any) {
      if (/re-authenticate|step.up|requires_step_up/i.test(String(ex?.message))) {
        setToast("Please sign in again to reveal. Sign out and back in.");
      } else {
        setToast(ex?.message ?? "Reveal failed.");
      }
      setTimeout(() => setToast(null), 3200);
    } finally {
      setRevealing(false);
    }
  }

  return (
    <div className={`sg-record is-${actionPillKind(event.actionResult)}`}>
      <div className="sg-record-meta">
        <div className="sg-record-time">
          {new Date(event.createdAt).toLocaleString([], { dateStyle: "short", timeStyle: "short" })}
        </div>
        <div className="sg-record-tool"><ToolBadge tool={event.destinationTool} size={12} /></div>
        <div className="sg-record-tool" style={{ color: "var(--sg-ink-5)" }}>User {event.userId.slice(0, 6)}…</div>
      </div>
      <div className="sg-record-body">
        <div className="sg-record-title sg-row" style={{ gap: 8, alignItems: "center" }}>
          <span>{event.explanation}</span>
          {event.encryptedFullPrompt && (
            <button
              type="button"
              className="sg-reveal-btn"
              onClick={toggleReveal}
              disabled={revealing}
              aria-label={revealed ? "Hide full prompt" : "Reveal full prompt"}
              title={revealed ? "Hide — auditable" : "Reveal — auditable"}
            >
              <EyeIcon open={!!revealed} />
            </button>
          )}
        </div>
        {revealed ? (
          <RedactedPreview fullText={revealed} spans={event.sensitiveSpans} />
        ) : (
          <RedactedPreview previewText={event.previewText} fallback={event.maskedPreview} />
        )}
        {event.rewrittenPrompt && (
          <div style={{ marginTop: 10 }}>
            <div className="sg-row" style={{ justifyContent: "space-between", marginBottom: 6 }}>
              <div className="sg-label">Suggested Rewrite</div>
              <div className="sg-muted" style={{ fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                {event.rewriteSource ? sourceLabel(event.rewriteSource) : "Rewrite"}
                {typeof event.rewriteConfidence === "number" && ` · ${confidenceWord(event.rewriteConfidence)} Confidence`}
                {event.transformations?.length ? ` · ${event.transformations.length} Change${event.transformations.length === 1 ? "" : "s"}` : ""}
              </div>
            </div>
            <div className="sg-redact" style={{ borderLeftColor: "var(--sg-blue)" }}>{event.rewrittenPrompt}</div>
          </div>
        )}
      </div>
      <div className="sg-record-actions">
        <Pill kind={actionPillKind(event.actionResult)}>{actionLabel(event.actionResult)}</Pill>
        <RiskPill score={event.riskScore} />
        {event.userDecision === "continued" && (
          <Pill kind="block" title="User chose Send Anyway after Ventrin flagged this prompt. Logged for audit.">
            Sent Anyway
          </Pill>
        )}
        {event.userDecision === "used_safe" && (
          <Pill kind="allow" title="User accepted the Safe Version.">Used Safe</Pill>
        )}
        {event.falseFlagReport && (
          <Pill kind="warn" title={`User reported as false flag: ${event.falseFlagReport.note ?? ""}`}>
            False Flag Reported
          </Pill>
        )}
      </div>
      {toast && <div className="sg-audit-toast">{toast}</div>}
    </div>
  );
}

function sourceLabel(s: string): string {
  if (s === "auto") return "Auto";
  if (s === "llm") return "Assisted";
  return "Fallback";
}
function confidenceWord(score: number): string {
  if (score >= 0.8) return "High";
  if (score >= 0.55) return "Medium";
  return "Low";
}

function EyeIcon({ open }: { open: boolean }) {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden>
      <path d="M8 3c3.5 0 6 3.2 6.5 5-0.5 1.8-3 5-6.5 5S2 9.8 1.5 8C2 6.2 4.5 3 8 3z" fill="none" stroke="currentColor" strokeWidth="1.2" />
      <circle cx="8" cy="8" r="1.9" fill="currentColor" />
      {!open && <path d="M2 2 L14 14" stroke="currentColor" strokeWidth="1.2" />}
    </svg>
  );
}
